import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { apiGetJson, apiPutJson } from "../utils/apiClient";
import LoadingInline from "../components/LoadingInline";
import { ToastViewport, useToast } from "../components/Toast";
import { useUserContext } from "../contexts/UserContext";
import { HelpTip } from "../components/HelpTip";
import { isFreePlanUser, isLightPlanUser } from "../utils/plan";

function isDemoModeLocal(): boolean {
    if (typeof window === "undefined") return false;
    if (window.location.pathname.startsWith("/demo")) return true;
    const ls = localStorage.getItem("demoMode");
    return ls === "1" || ls === "true";
}

const pad2 = (n: number) => String(n).padStart(2, "0");
function clamp(n: number, min: number, max: number) {
    return Math.min(Math.max(n, min), max);
}
function normalizeStartMonth(v: any): number {
    const n = Number(v);
    if (!Number.isFinite(n)) return 1;
    return clamp(Math.trunc(n), 1, 12);
}

function computeDefaultYearForStartMonth(now: dayjs.Dayjs, startMonth: number) {
    const sm = normalizeStartMonth(startMonth);
    const startThisYear = dayjs(`${now.year()}-${pad2(sm)}-01`);
    return now.isBefore(startThisYear) ? now.year() - 1 : now.year();
}

type MonthForm = any;
type ApplyScope = "all" | "from" | "range";

// 楽天外広告費の按分期間（日次利益用）。フォーム上は「日」(1〜月末)で持ち、保存時に日付へ変換する
const AD_PERIOD_DAY_FIELDS = [1, 2, 3].flatMap((i) => [
    `non_rakuten_ad_${i}_start_day`,
    `non_rakuten_ad_${i}_end_day`,
]);
/** "YYYY-MM-DD" → 日(1〜31)。空/不正は null */
function dateStrToDay(v: any): number | null {
    const str = v == null ? "" : typeof v === "object" && typeof v.value === "string" ? v.value : String(v);
    const m = str.match(/^\d{4}-\d{2}-(\d{2})/);
    if (!m) return null;
    const d = Number(m[1]);
    return Number.isInteger(d) && d >= 1 && d <= 31 ? d : null;
}

const LEFT_W = "w-[320px] min-w-[320px] max-w-[320px]";
const STICKY_TH = `sticky left-0 z-40 bg-gray-100 ${LEFT_W}`;
const STICKY_TD = `sticky left-0 z-30 bg-white ${LEFT_W}`;
const STICKY_TD_GRAY = `sticky left-0 z-30 bg-gray-50 ${LEFT_W}`;

const TABLE_CLASS = "min-w-[1200px] w-full text-sm border-separate border-spacing-0";

const TH_CELL = "p-1 border whitespace-nowrap bg-gray-100";
const TD_CELL = "p-0.5 border bg-white align-top";
const TD_CELL_GRAY = "p-0.5 border bg-gray-50 align-top";

const UI_INPUT_NUM_CELL = "w-full border px-1 py-0 text-xs leading-none h-7 text-right";

const UI_SELECT_CTRL = "border px-2 py-0.5 rounded bg-white text-sm h-7";
const UI_BTN_SM = "px-3 py-1 border bg-white rounded hover:bg-gray-50 disabled:opacity-60";
const UI_BTN_PRIMARY =
    "px-4 py-1.5 bg-blue-600 text-white rounded shadow-sm hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed";

function LeftCell({
    children,
    gray = false,
    className = "",
}: {
    children: React.ReactNode;
    gray?: boolean;
    className?: string;
}) {
    return (
        <td className={`p-1 border font-medium ${gray ? STICKY_TD_GRAY : STICKY_TD} ${className}`}>
            {children}
        </td>
    );
}

const SETTINGS_ENDPOINT = "/api/company/settings/fiscal";

// 確定値上書き対象の科目定義
// key は BQ カラム名のプレフィックス（<key>_cost_confirmed / <key>_auto）
const RAKUTEN_PROMO_ITEMS: { key: string; label: string }[] = [
    { key: "point", label: "ポイント費用" },
    { key: "mail", label: "メルマガ費用" },
    { key: "afl", label: "アフィリエイト費用" },
];
const RAKUTEN_AD_ITEMS: { key: string; label: string }[] = [
    { key: "rpp", label: "RPP広告" },
    { key: "rppexp", label: "RPP-EXP広告" },
    { key: "cpa", label: "楽天CPA広告" },
    { key: "cpnadv", label: "クーポンアドバンス広告" },
    { key: "tda", label: "TDA広告" },
    { key: "tdaexp", label: "TDA-EXP広告" },
    { key: "ec", label: "純広告" },
    { key: "deal", label: "DEAL成果報酬" },
];
// 物流費（確定値上書き対象）
// shipping = 配送料の月合計（入力すると推計値を置き換え）/ shipping_other = 加算される物流費
const RAKUTEN_SHIPPING_ITEMS: { key: string; label: string }[] = [
    { key: "shipping", label: "配送料（月合計）" },
    { key: "shipping_other", label: "その他物流費（作業料・資材・保管料など）" },
];
const CONFIRMED_FIELDS = [...RAKUTEN_PROMO_ITEMS, ...RAKUTEN_AD_ITEMS, ...RAKUTEN_SHIPPING_ITEMS].map(
    (it) => `${it.key}_cost_confirmed`
);

type ConfirmedGroup = {
    no: string;
    title: string;
    items: { key: string; label: string }[];
    help?: React.ReactNode;
};

async function fetchFiscalStartMonth(shopUrl: string): Promise<number | null> {
    const d = await apiGetJson<any>(`${SETTINGS_ENDPOINT}?shop_url=${encodeURIComponent(shopUrl)}`);
    const m = d?.fiscalMonth ?? null;
    return m == null ? null : Number(m);
}


export default function VariableCostSettingsPage({ shopUrl }: { shopUrl: string }) {
    const user = useUserContext();

    const isDemo = useMemo(() => isDemoModeLocal(), []);

    const [templateOpen, setTemplateOpen] = useState(false);

    const shopUrlSafe = useMemo(() => {
        const trimmed = String(shopUrl ?? "").trim();
        if (trimmed) return trimmed;
        return isDemo ? "demo-shop" : "";
    }, [shopUrl, isDemo]);

    const isRestrictedPlan = useMemo(() => isFreePlanUser(user, shopUrlSafe) || isLightPlanUser(user, shopUrlSafe), [user, shopUrlSafe]);
    const missingShopUrl = !isDemo && !shopUrlSafe;

    const [settingsLoading, setSettingsLoading] = useState(true);
    const [startMonth, setStartMonth] = useState<number>(1);

    const userChangedYearRef = useRef(false);
    const [year, setYear] = useState<number>(() => computeDefaultYearForStartMonth(dayjs(), 1));

    const loadTokenRef = useRef(0);

    // 楽天広告費の速報値（RMS自動取得）表示用: ym -> { rpp_auto, ... }
    const [autoAdByMonth, setAutoAdByMonth] = useState<Record<string, any>>({});
    // ロード時点の確定値スナップショット（クリア保存の判定用）
    const loadedConfirmedRef = useRef<Record<string, Record<string, number | null>>>({});

    const [dirty, setDirty] = useState(false);
    const [dirtyHintDismissed, setDirtyHintDismissed] = useState(false);
    useEffect(() => {
        if (dirty) setDirtyHintDismissed(false);
    }, [dirty]);

    /** ✅ 未保存ガード：タブ閉じ/リロード時にブラウザ標準の確認を出す（固定費入力と同じ） */
    useEffect(() => {
        if (!dirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    /** ✅ 未保存ガード：年度切替の前に確認（固定費入力と同じ） */
    const confirmDiscardIfDirty = () => {
        if (!dirty) return true;
        return window.confirm("未保存の変更があります。\n移動すると入力内容は破棄されます。よろしいですか？");
    };

    const { toasts, pushToast, removeToast } = useToast();

    useEffect(() => {
        loadTokenRef.current++;
        setDataByMonth({});
        setAutoAdByMonth({});
        loadedConfirmedRef.current = {};
        setError(null);
        userChangedYearRef.current = false;
        setDirty(false);
        setDirtyHintDismissed(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shopUrlSafe]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            setSettingsLoading(true);

            try {
                if (isDemo) {
                    const sm = 1;
                    if (cancelled) return;
                    setStartMonth(sm);
                    setYear(computeDefaultYearForStartMonth(dayjs(), sm));
                    return;
                }

                if (!shopUrlSafe) {
                    if (!cancelled) {
                        setStartMonth(1);
                        if (!userChangedYearRef.current) setYear(computeDefaultYearForStartMonth(dayjs(), 1));
                    }
                    return;
                }

                const raw = await fetchFiscalStartMonth(shopUrlSafe);
                const sm = normalizeStartMonth(raw ?? 1);

                if (cancelled) return;

                setStartMonth(sm);

                if (!userChangedYearRef.current) {
                    setYear(computeDefaultYearForStartMonth(dayjs(), sm));
                }
            } catch {
                if (!cancelled) {
                    setStartMonth(1);
                    if (!userChangedYearRef.current) setYear(computeDefaultYearForStartMonth(dayjs(), 1));
                }
            } finally {
                if (!cancelled) setSettingsLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [shopUrlSafe, isDemo]);

    const months = useMemo(() => {
        const start = dayjs(`${year}-${pad2(startMonth)}-01`);
        return Array.from({ length: 12 }, (_, i) => start.add(i, "month"));
    }, [year, startMonth]);

    const monthKeyList = useMemo(() => months.map((m) => m.format("YYYY-MM")), [months]);

    const periodLabel = useMemo(() => {
        const s = months[0];
        const e = months[months.length - 1];
        if (!s || !e) return "";
        return `${s.format("YYYY/MM")}〜${e.format("YYYY/MM")}`;
    }, [months]);

    const baseYear = useMemo(() => computeDefaultYearForStartMonth(dayjs(), startMonth), [startMonth]);
    const yearOptions = useMemo(() => Array.from({ length: 6 }, (_, i) => baseYear - 2 + i), [baseYear]);

    const emptyForm: MonthForm = useMemo(
        () => ({
            courier_shipping_cost: null,
            mail_shipping_cost: null,
            logistics_cost: null,
            non_rakuten_ad_1_cost: null,
            non_rakuten_ad_2_cost: null,
            non_rakuten_ad_3_cost: null,
            ...Object.fromEntries(AD_PERIOD_DAY_FIELDS.map((f) => [f, null])),
            ...Object.fromEntries(CONFIRMED_FIELDS.map((f) => [f, null])),
        }),
        []
    );

    // 楽天外広告費の科目名（全月共通）
    const [adSlotNames, setAdSlotNames] = useState<[string, string, string]>(["", "", ""]);
    // 表示するスロット数（0〜3、「＋ 科目を追加」で増加）
    const [activeAdSlots, setActiveAdSlots] = useState(0);

    const [dataByMonth, setDataByMonth] = useState<Record<string, MonthForm>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const savingRef = useRef(false);

    function parseIntOrNullStrict(raw: string) {
        const s = String(raw ?? "").trim();
        if (s === "") return { ok: true as const, value: null as number | null };
        if (!/^\d+$/.test(s)) {
            return { ok: false as const, reason: "整数のみ入力できます（小数点は不可）" };
        }
        const n = Number(s);
        if (!Number.isSafeInteger(n)) {
            return { ok: false as const, reason: "数値が大きすぎます" };
        }
        return { ok: true as const, value: n };
    }

    function handleIntChange(ym: string, field: string, raw: string) {
        const r = parseIntOrNullStrict(raw);
        if (!r.ok) {
            pushToast("error", r.reason);
            return;
        }
        setDirty(true);
        setDataByMonth((prev) => ({
            ...prev,
            [ym]: {
                ...(prev[ym] || { ...emptyForm }),
                [field]: r.value,
            },
        }));
    }

    /** 楽天外広告費の按分期間（開始日/終了日）の入力。空=未指定、1〜月末日のみ許可 */
    function handleDayChange(ym: string, field: string, raw: string, daysInMonth: number) {
        const s = String(raw ?? "").trim();
        let value: number | null = null;
        if (s !== "") {
            if (!/^\d+$/.test(s)) {
                pushToast("error", "日付は整数で入力してください");
                return;
            }
            const n = Number(s);
            if (n < 1 || n > daysInMonth) {
                pushToast("error", `日付は 1〜${daysInMonth} の範囲で入力してください`);
                return;
            }
            value = n;
        }
        setDirty(true);
        setDataByMonth((prev) => ({
            ...prev,
            [ym]: {
                ...(prev[ym] || { ...emptyForm }),
                [field]: value,
            },
        }));
    }

    // 範囲適用
    const [applyScope, setApplyScope] = useState<ApplyScope>("all");
    const [applyFromYm, setApplyFromYm] = useState<string>(monthKeyList[0] || "");
    const [applyToYm, setApplyToYm] = useState<string>(monthKeyList[monthKeyList.length - 1] || "");

    useEffect(() => {
        setApplyFromYm(monthKeyList[0] || "");
        setApplyToYm(monthKeyList[monthKeyList.length - 1] || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monthKeyList.join("|")]);

    const resolveTargetMonths = () => {
        if (monthKeyList.length === 0) return [];
        if (applyScope === "all") return [...monthKeyList];

        const fromIdx = clamp(monthKeyList.indexOf(applyFromYm), 0, monthKeyList.length - 1);
        if (applyScope === "from") return monthKeyList.slice(fromIdx);

        const toIdxRaw = monthKeyList.indexOf(applyToYm);
        const toIdx = clamp(toIdxRaw < 0 ? monthKeyList.length - 1 : toIdxRaw, 0, monthKeyList.length - 1);
        const a = Math.min(fromIdx, toIdx);
        const b = Math.max(fromIdx, toIdx);
        return monthKeyList.slice(a, b + 1);
    };

    const [tplLogisticsCost, setTplLogisticsCost] = useState<string>("");
    const [tplCourierShippingCost, setTplCourierShippingCost] = useState<string>("");
    const [tplMailShippingCost, setTplMailShippingCost] = useState<string>("");
    const [tplAdCosts, setTplAdCosts] = useState<[string, string, string]>(["", "", ""]);

    function parseTemplateMoney(raw: string) {
        const s = String(raw ?? "").trim();
        if (s === "") return null;
        if (!/^-?\d+$/.test(s)) return null;
        const n = Number(s);
        return Number.isSafeInteger(n) ? n : null;
    }

    const applyTemplateToRange = () => {
        const targets = resolveTargetMonths();
        if (targets.length === 0) return;

        setDirty(true);
        setDataByMonth((prev) => {
            const next: Record<string, MonthForm> = { ...prev };
            for (const ym of targets) {
                const cur = prev[ym] || { ...emptyForm };
                const updated = { ...cur };
                if (isRestrictedPlan) {
                    const v = parseTemplateMoney(tplLogisticsCost);
                    if (v !== null) updated.logistics_cost = v;
                } else {
                    const vc = parseTemplateMoney(tplCourierShippingCost);
                    const vm = parseTemplateMoney(tplMailShippingCost);
                    if (vc !== null) updated.courier_shipping_cost = vc;
                    if (vm !== null) updated.mail_shipping_cost = vm;
                }
                for (let i = 0; i < activeAdSlots; i++) {
                    const key = `non_rakuten_ad_${i + 1}_cost`;
                    const v = parseTemplateMoney(tplAdCosts[i]);
                    if (v !== null) updated[key] = v;
                }
                next[ym] = updated;
            }
            return next;
        });

        pushToast("success", `複数月一括適用を ${targets.length} ヶ月に適用しました`);
    };

    const isFormEmpty = (m: MonthForm): boolean => {
        const numFields = ["courier_shipping_cost", "mail_shipping_cost", "logistics_cost"];
        return numFields.every((k) => m?.[k] === null || m?.[k] === undefined);
    };

    const loadAll = async () => {
        if (missingShopUrl) {
            setError("shopUrl が未指定です（このページは shopUrl が必要です）");
            pushToast("error", "shopUrl が未指定です");
            return;
        }

        const token = ++loadTokenRef.current;
        const currentShopUrl = shopUrlSafe;
        const currentMonths = [...monthKeyList];

        setLoading(true);
        setError(null);

        try {
            const pairs = await Promise.all(
                currentMonths.map(async (ym) => {
                    try {
                        const d = await apiGetJson<any>(
                            `/api/rakuten-expense?shop_url=${encodeURIComponent(currentShopUrl)}&year_month=${ym}`
                        );
                        const form: MonthForm = {
                            courier_shipping_cost: d?.courier_shipping_cost ?? null,
                            mail_shipping_cost: d?.mail_shipping_cost ?? null,
                            logistics_cost: d?.logistics_cost ?? null,
                            non_rakuten_ad_1_cost: d?.non_rakuten_ad_1_cost ?? null,
                            non_rakuten_ad_2_cost: d?.non_rakuten_ad_2_cost ?? null,
                            non_rakuten_ad_3_cost: d?.non_rakuten_ad_3_cost ?? null,
                            non_rakuten_ad_1_start_day: dateStrToDay(d?.non_rakuten_ad_1_start_date),
                            non_rakuten_ad_1_end_day: dateStrToDay(d?.non_rakuten_ad_1_end_date),
                            non_rakuten_ad_2_start_day: dateStrToDay(d?.non_rakuten_ad_2_start_date),
                            non_rakuten_ad_2_end_day: dateStrToDay(d?.non_rakuten_ad_2_end_date),
                            non_rakuten_ad_3_start_day: dateStrToDay(d?.non_rakuten_ad_3_start_date),
                            non_rakuten_ad_3_end_day: dateStrToDay(d?.non_rakuten_ad_3_end_date),
                            ...Object.fromEntries(CONFIRMED_FIELDS.map((f) => [f, d?.[f] ?? null])),
                            // 名前はロード後に別途集約するため一時保持
                            _non_rakuten_ad_1_name: d?.non_rakuten_ad_1_name ?? null,
                            _non_rakuten_ad_2_name: d?.non_rakuten_ad_2_name ?? null,
                            _non_rakuten_ad_3_name: d?.non_rakuten_ad_3_name ?? null,
                        };
                        return [ym, form] as const;
                    } catch (e: any) {
                        console.warn("[VariableCostSettingsPage] load failed:", ym, e);
                        return [ym, { ...emptyForm }] as const;
                    }
                })
            );

            // 速報値（RMS自動取得の広告費）を取得（失敗しても入力自体は可能にする）
            let autoRows: any[] = [];
            try {
                autoRows = await apiGetJson<any[]>(
                    `/api/rakuten-expense/ad-auto?shop_url=${encodeURIComponent(currentShopUrl)}&from=${currentMonths[0]}&to=${currentMonths[currentMonths.length - 1]}`
                ) ?? [];
            } catch (e) {
                console.warn("[VariableCostSettingsPage] ad-auto load failed:", e);
            }

            if (token !== loadTokenRef.current) return;

            const next: Record<string, MonthForm> = {};
            for (const [ym, form] of pairs) next[ym] = form;
            setDataByMonth(next);

            const autoNext: Record<string, any> = {};
            for (const r of autoRows) {
                if (r?.year_month) autoNext[String(r.year_month)] = r;
            }
            setAutoAdByMonth(autoNext);

            // クリア保存判定用にロード時点の確定値を控える
            const confirmedSnapshot: Record<string, Record<string, number | null>> = {};
            for (const [ym, form] of pairs) {
                confirmedSnapshot[ym] = Object.fromEntries(
                    CONFIRMED_FIELDS.map((f) => [f, form[f] ?? null])
                ) as Record<string, number | null>;
            }
            loadedConfirmedRef.current = confirmedSnapshot;

            // 広告費の科目名を集約（最初に見つかった非空値を採用）
            const names: [string, string, string] = ["", "", ""];
            let active = 0;
            for (const [, form] of pairs) {
                for (let i = 1; i <= 3; i++) {
                    const n = form[`_non_rakuten_ad_${i}_name`];
                    if (!names[i - 1] && n) {
                        names[i - 1] = String(n);
                        active = Math.max(active, i);
                    }
                }
            }
            setAdSlotNames(names);
            setActiveAdSlots(active);

            setDirty(false);
            setDirtyHintDismissed(false);
        } catch (e: any) {
            if (token !== loadTokenRef.current) return;
            setError(e?.message || "load error");
            pushToast("error", e?.message || "読み込みに失敗しました");
            setDataByMonth({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (settingsLoading) return;
        if (missingShopUrl) return;
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoading, shopUrlSafe, monthKeyList.join("|")]);

    const handleSaveAll = async () => {
        if (missingShopUrl) {
            pushToast("error", "shopUrl が未指定です（保存できません）");
            return;
        }

        // 科目名が空のまま金額だけ入っていると、利益からは引かれるのに内訳に出ないため保存させない
        const namelessSlot = Array.from({ length: activeAdSlots }, (_, idx) => idx).find((idx) => {
            if (String(adSlotNames[idx] ?? "").trim() !== "") return false;
            const slotKey = `non_rakuten_ad_${idx + 1}_cost`;
            return monthKeyList.some((ym) => dataByMonth[ym]?.[slotKey] != null);
        });
        if (namelessSlot !== undefined) {
            pushToast(
                "error",
                `楽天外広告費${namelessSlot + 1}行目の科目名を入力してください（科目名が空だと金額を保存できません）`
            );
            return;
        }

        // 按分期間の整合性（開始日 <= 終了日、月内）。空欄は月全体に均等按分
        for (const ym of monthKeyList) {
            const m = dataByMonth[ym] || {};
            const dim = dayjs(`${ym}-01`).daysInMonth();
            for (let i = 1; i <= activeAdSlots; i++) {
                const sd = m[`non_rakuten_ad_${i}_start_day`] as number | null;
                const ed = m[`non_rakuten_ad_${i}_end_day`] as number | null;
                if ((sd != null && (sd < 1 || sd > dim)) || (ed != null && (ed < 1 || ed > dim))) {
                    pushToast("error", `${ym} の楽天外広告費${i}行目: 日付は 1〜${dim} の範囲で入力してください`);
                    return;
                }
                if (sd != null && ed != null && sd > ed) {
                    pushToast("error", `${ym} の楽天外広告費${i}行目: 開始日が終了日より後になっています`);
                    return;
                }
            }
        }

        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);

        const token = ++loadTokenRef.current;
        const currentShopUrl = shopUrlSafe;
        const currentMonths = [...monthKeyList];

        try {
            // RN-246: 12か月分を1回でまとめて保存する（1か月ずつ12本同時だと、書き込みの順番待ちで10〜18秒かかっていた）
            const variableMonths = currentMonths
                .filter((ym) => {
                    const m = dataByMonth[ym] || { ...emptyForm };
                    const hasAdData = activeAdSlots > 0;
                    return !(isFormEmpty(m) && !hasAdData);
                })
                .map((ym) => {
                    const m = dataByMonth[ym] || { ...emptyForm };
                    const ymDate = `${ym}-01`;
                    return {
                        year_month: ymDate,
                        courier_shipping_cost: m.courier_shipping_cost,
                        mail_shipping_cost: m.mail_shipping_cost,
                        logistics_cost: m.logistics_cost,
                        non_rakuten_ad_1_name: adSlotNames[0] || null,
                        non_rakuten_ad_1_cost: m.non_rakuten_ad_1_cost,
                        non_rakuten_ad_2_name: adSlotNames[1] || null,
                        non_rakuten_ad_2_cost: m.non_rakuten_ad_2_cost,
                        non_rakuten_ad_3_name: adSlotNames[2] || null,
                        non_rakuten_ad_3_cost: m.non_rakuten_ad_3_cost,
                        ...Object.fromEntries(
                            [1, 2, 3].flatMap((i) =>
                                (["start", "end"] as const).map((k) => {
                                    const day = m[`non_rakuten_ad_${i}_${k}_day`] as number | null;
                                    return [
                                        `non_rakuten_ad_${i}_${k}_date`,
                                        day != null ? `${ym}-${pad2(day)}` : null,
                                    ];
                                })
                            )
                        ),
                    };
                });
            if (variableMonths.length > 0) {
                await apiPutJson("/api/rakuten-expense/variable/months", {
                    shop_url: currentShopUrl,
                    months: variableMonths,
                });
            }

            // 楽天広告費の確定値（値がある月・ロード時に値があった月＝クリアされた月のみ送信）
            const confirmedMonths = currentMonths
                .filter((ym) => {
                    const m = dataByMonth[ym] || {};
                    const loaded = loadedConfirmedRef.current[ym] || {};
                    return CONFIRMED_FIELDS.some(
                        (f) => (m as any)[f] != null || loaded[f] != null
                    );
                })
                .map((ym) => {
                    const m = dataByMonth[ym] || {};
                    return {
                        year_month: `${ym}-01`,
                        ...Object.fromEntries(CONFIRMED_FIELDS.map((f) => [f, (m as any)[f] ?? null])),
                    };
                });
            if (confirmedMonths.length > 0) {
                await apiPutJson("/api/rakuten-expense/ad-confirmed", {
                    shop_url: currentShopUrl,
                    months: confirmedMonths,
                });
            }

            if (token !== loadTokenRef.current) return;

            pushToast("success", "12ヶ月分を保存しました");
            setDirty(false);
            setDirtyHintDismissed(false);

            loadAll();
        } catch (e: any) {
            if (token !== loadTokenRef.current) return;
            console.warn("[VariableCostSettingsPage] save failed:", e);
            pushToast("error", e?.message || "保存に失敗しました");
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    // =====================================================
    // ⑥ 過去ポイント登録（日別）
    // RMSのポイント付与明細は過去6ヶ月分しか取得できないため、
    // それより前の期間を日別で手動登録する（/api/rpoint-manual）
    // =====================================================
    type RpDay = { point_count: string; point_amount: string };
    const RP_WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

    // 自動取得の境界（当月1日 − 6ヶ月）。これより前の月のみ手動登録可
    const rpBoundary = useMemo(() => dayjs().startOf("month").subtract(6, "month"), []);
    const rpMonthOptions = useMemo(
        () => Array.from({ length: 36 }, (_, i) => rpBoundary.subtract(i + 1, "month").format("YYYY-MM")),
        [rpBoundary]
    );

    const [rpOpen, setRpOpen] = useState(false);
    const [rpMonth, setRpMonth] = useState<string>(rpMonthOptions[0]);
    const [rpDays, setRpDays] = useState<Record<string, RpDay>>({});
    const [rpLoading, setRpLoading] = useState(false);
    const [rpSaving, setRpSaving] = useState(false);
    const [rpDirty, setRpDirty] = useState(false);
    const [rpPaste, setRpPaste] = useState("");
    const [rpKarteMinDate, setRpKarteMinDate] = useState<string | null>(null);
    // 読み込み成功済みの月。未ロード月の保存を防ぐ（既存行が見えないまま全削除になるため）
    const [rpLoadedMonth, setRpLoadedMonth] = useState<string | null>(null);
    const rpLoadTokenRef = useRef(0);

    const rpDateList = useMemo(() => {
        const daysInMonth = dayjs(`${rpMonth}-01`).daysInMonth();
        return Array.from({ length: daysInMonth }, (_, i) => `${rpMonth}-${pad2(i + 1)}`);
    }, [rpMonth]);

    const loadRpMonth = async () => {
        if (missingShopUrl || !rpMonth) return;
        const token = ++rpLoadTokenRef.current;
        setRpLoading(true);
        try {
            const d = await apiGetJson<any>(
                `/api/rpoint-manual?shop_url=${encodeURIComponent(shopUrlSafe)}&year_month=${rpMonth}`
            );
            if (token !== rpLoadTokenRef.current) return;
            const next: Record<string, RpDay> = {};
            for (const r of d?.days ?? []) {
                next[String(r.purchase_date)] = {
                    point_count: r.point_count == null ? "" : String(r.point_count),
                    point_amount: r.point_amount == null ? "" : String(r.point_amount),
                };
            }
            setRpDays(next);
            setRpKarteMinDate(d?.karte_min_date ?? null);
            setRpLoadedMonth(rpMonth);
            setRpDirty(false);
        } catch (e: any) {
            if (token !== rpLoadTokenRef.current) return;
            setRpLoadedMonth(null);
            console.warn("[VariableCostSettingsPage] rpoint-manual load failed:", e);
            pushToast("error", e?.message || "過去ポイントの読み込みに失敗しました");
        } finally {
            if (token === rpLoadTokenRef.current) setRpLoading(false);
        }
    };

    useEffect(() => {
        if (!rpOpen) return;
        loadRpMonth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rpOpen, shopUrlSafe, rpMonth]);

    const handleRpChange = (date: string, field: keyof RpDay, value: string) => {
        setRpDirty(true);
        setRpDays((prev) => ({
            ...prev,
            [date]: { point_count: "", point_amount: "", ...prev[date], [field]: value },
        }));
    };

    // RMS画面のコピペを日別に反映（例: 2025/01/15(水) 123 4,567）
    const handleRpApplyPaste = () => {
        let applied = 0;
        let skipped = 0;
        const updates: Record<string, RpDay> = {};
        for (const line of rpPaste.split(/\r?\n/)) {
            const dm = line.match(/(\d{4})[/\-年]\s*(\d{1,2})[/\-月]\s*(\d{1,2})/);
            if (!dm) continue;
            const date = `${dm[1]}-${pad2(Number(dm[2]))}-${pad2(Number(dm[3]))}`;
            const rest = line.slice((dm.index ?? 0) + dm[0].length);
            const nums = (rest.match(/[\d,]+/g) ?? [])
                .map((s) => s.replace(/,/g, ""))
                .filter((s) => /^\d+$/.test(s));
            if (nums.length === 0) continue;
            if (!date.startsWith(`${rpMonth}-`)) {
                skipped++;
                continue;
            }
            updates[date] =
                nums.length === 1
                    ? { point_count: "", point_amount: nums[0] }
                    : { point_count: nums[0], point_amount: nums[1] };
            applied++;
        }
        if (applied === 0) {
            pushToast("error", skipped > 0 ? `対象月（${rpMonth}）以外の日付のためスキップしました` : "貼り付けデータから日付と数値を読み取れませんでした");
            return;
        }
        setRpDays((prev) => ({ ...prev, ...updates }));
        setRpDirty(true);
        setRpPaste("");
        pushToast("success", `${applied} 日分を反映しました${skipped > 0 ? `（対象月外 ${skipped} 行はスキップ）` : ""}`);
    };

    const handleRpSave = async () => {
        if (missingShopUrl) {
            pushToast("error", "shopUrl が未指定です（保存できません）");
            return;
        }
        if (rpLoadedMonth !== rpMonth) {
            pushToast("error", "この月の読み込みが完了していません。月を選び直してから保存してください");
            return;
        }
        setRpSaving(true);
        try {
            const days = rpDateList.map((date) => {
                const d = rpDays[date];
                const count = d?.point_count?.trim() ?? "";
                const amount = d?.point_amount?.trim() ?? "";
                return {
                    purchase_date: date,
                    point_count: count === "" ? null : Number(count),
                    point_amount: amount === "" ? null : Number(amount),
                };
            });
            await apiPutJson("/api/rpoint-manual", {
                shop_url: shopUrlSafe,
                year_month: rpMonth,
                days,
            });
            pushToast("success", `${rpMonth.replace("-", "/")} の過去ポイントを保存しました`);
            setRpDirty(false);
            loadRpMonth();
        } catch (e: any) {
            console.warn("[VariableCostSettingsPage] rpoint-manual save failed:", e);
            pushToast("error", e?.message || "過去ポイントの保存に失敗しました");
        } finally {
            setRpSaving(false);
        }
    };

    const rpMonthTotal = useMemo(
        () =>
            rpDateList.reduce((sum, date) => {
                const v = Number(rpDays[date]?.point_amount);
                return sum + (Number.isFinite(v) ? v : 0);
            }, 0),
        [rpDateList, rpDays]
    );

    const calcTotal = (form: MonthForm) => {
        const fields = isRestrictedPlan
            ? ["logistics_cost"]
            : ["courier_shipping_cost", "mail_shipping_cost"];
        return fields.reduce((sum, key) => {
            const v = Number(form?.[key]);
            return sum + (isNaN(v) ? 0 : v);
        }, 0);
    };

    const totalsByMonth = useMemo(() => {
        const out: Record<string, number> = {};
        for (const ym of monthKeyList) out[ym] = calcTotal(dataByMonth[ym] || emptyForm);
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataByMonth, monthKeyList.join("|"), emptyForm, isRestrictedPlan]);

    const overlayBusy = settingsLoading || (loading && Object.keys(dataByMonth).length === 0);

    const renderConfirmedGroup = (group: ConfirmedGroup) => (
        <React.Fragment key={group.no}>
            <div className="mb-1 text-sm font-semibold text-gray-700 flex items-center gap-1">
                {group.no} {group.title}
                <HelpTip
                    text={
                        group.help ?? (
                            <div className="text-xs leading-relaxed">
                                RMSから自動取得した費用（速報値）と実際の請求額に差分がある場合に、確定値を入力してください。<br />
                                確定値を入力した月は、月次利益が確定値で計算されます。空欄の月は速報値のまま計算されます。<br />
                                ※確定値は月次利益にのみ反映されます（日次利益は速報値のままです）。<br />
                                ※金額は店舗の税表示設定（税込/税抜）に合わせて入力してください。<br />
                                ※速報値も、店舗の税表示設定（税込/税抜）に合わせた金額で表示しています。
                            </div>
                        )
                    }
                />
            </div>
            <div className="overflow-x-auto border rounded relative mb-4">
                <table className={TABLE_CLASS}>
                    <thead className="bg-gray-100">
                        <tr>
                            <th className={`text-left p-1 border ${STICKY_TH}`}>科目 / 区分</th>
                            {months.map((m) => (
                                <th key={m.format("YYYY-MM")} className={`text-center ${TH_CELL}`}>
                                    {m.format("YYYY/MM")}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {group.items.map((item) => {
                            const confirmedKey = `${item.key}_cost_confirmed`;
                            const autoKey = `${item.key}_auto`;
                            return (
                                <React.Fragment key={item.key}>
                                    <tr>
                                        <LeftCell gray>
                                            <div className="flex items-center justify-between gap-1">
                                                <span>{item.label}</span>
                                                <span className="text-[10px] text-gray-500 whitespace-nowrap">速報値（自動取得）</span>
                                            </div>
                                        </LeftCell>
                                        {monthKeyList.map((ym) => {
                                            const v = autoAdByMonth[ym]?.[autoKey];
                                            return (
                                                <td key={ym} className={`${TD_CELL_GRAY} text-right text-xs text-gray-500 pr-1 whitespace-nowrap`}>
                                                    {v != null ? Number(v).toLocaleString() : "−"}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    <tr>
                                        <LeftCell>
                                            <span className="text-xs text-gray-600 pl-3">└ 確定値（請求額）</span>
                                        </LeftCell>
                                        {monthKeyList.map((ym) => (
                                            <td key={ym} className={TD_CELL}>
                                                <input
                                                    type="number"
                                                    className={UI_INPUT_NUM_CELL}
                                                    value={dataByMonth[ym]?.[confirmedKey] ?? ""}
                                                    onChange={(e) => handleIntChange(ym, confirmedKey, e.target.value)}
                                                    disabled={missingShopUrl}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </React.Fragment>
    );

    return (
        <div className="relative">
            {/* トースト */}
            <ToastViewport toasts={toasts} onClose={removeToast}>
                {dirty && !dirtyHintDismissed && !saving && (
                    <div className="pointer-events-auto">
                        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 shadow">
                            <div className="text-xs text-amber-900">変更があります。右上の「12ヶ月分を保存」を押してください。</div>
                            <button
                                className="ml-2 text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
                                onClick={() => setDirtyHintDismissed(true)}
                                aria-label="閉じる"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}
            </ToastViewport>

            {overlayBusy && (
                <div className="fixed top-14 bottom-0 left-0 md:left-[210px] right-0 z-50 flex items-center justify-center bg-white/0">
                    <LoadingInline />
                </div>
            )}

            <div className="max-w-full">
                {missingShopUrl && (
                    <div className="mb-2 text-sm text-red-600">shopUrl が未指定です（このページは shopUrl が必要です）</div>
                )}

                {/* 上部コントロール */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-lg font-semibold">変動費（年度 / 12ヶ月）</div>

                        <label className="text-sm text-gray-600 ml-2">年度</label>
                        <select
                            className={UI_SELECT_CTRL}
                            value={year}
                            onChange={(e) => {
                                if (!confirmDiscardIfDirty()) return;
                                userChangedYearRef.current = true;
                                setYear(Number(e.target.value));
                            }}
                            disabled={settingsLoading}
                        >
                            {yearOptions.map((y) => (
                                <option key={y} value={y}>
                                    {y}年度
                                </option>
                            ))}
                        </select>

                        <div className="text-sm text-gray-600 ml-2">
                            期首月: {startMonth}月 / 表示期間: {periodLabel}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                if (!confirmDiscardIfDirty()) return;
                                loadAll();
                            }}
                            disabled={loading || missingShopUrl}
                            className={UI_BTN_SM}
                        >
                            再読込
                        </button>

                        <button
                            onClick={handleSaveAll}
                            disabled={saving || missingShopUrl}
                            className={`${UI_BTN_PRIMARY} ${dirty ? "ring-2 ring-amber-400" : ""}`}
                        >
                            {saving ? "保存中..." : "12ヶ月分を保存"}
                        </button>
                    </div>
                </div>

                {error && <div className="mb-2 text-sm text-red-600">{error}</div>}

                {/* 複数月一括適用 */}
                <div className="mb-3 p-2 border rounded bg-gray-50">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <div className="font-semibold">複数月の変動費を一括で変更（必要な範囲に適用）</div>
                            <HelpTip
                                text={
                                    <div className="text-xs leading-relaxed">
                                        初回入力時に複数の月にデータを登録したい場合にご活用ください。
                                    </div>
                                }
                            />
                        </div>

                        <button
                            type="button"
                            className="h-8 w-8 inline-flex items-center justify-center rounded border bg-white hover:bg-gray-50"
                            onClick={() => setTemplateOpen((v) => !v)}
                            aria-expanded={templateOpen}
                            aria-label={templateOpen ? "折りたたむ" : "開く"}
                            title={templateOpen ? "折りたたむ" : "開く"}
                        >
                            <span className={`transition-transform ${templateOpen ? "rotate-180" : "rotate-0"}`}>
                                ▼
                            </span>
                        </button>
                    </div>

                    {templateOpen && (
                        <>
                            <div className="flex flex-wrap items-center gap-2 mb-2 mt-2">
                                <label className="text-sm text-gray-700">適用範囲</label>
                                <select
                                    className={UI_SELECT_CTRL}
                                    value={applyScope}
                                    onChange={(e) => setApplyScope(e.target.value as ApplyScope)}
                                >
                                    <option value="all">全月</option>
                                    <option value="from">この月以降</option>
                                    <option value="range">範囲</option>
                                </select>

                                {applyScope !== "all" && (
                                    <select
                                        className={UI_SELECT_CTRL}
                                        value={applyFromYm}
                                        onChange={(e) => setApplyFromYm(e.target.value)}
                                    >
                                        {monthKeyList.map((ym) => (
                                            <option key={ym} value={ym}>
                                                {dayjs(`${ym}-01`).format("YYYY/MM")}
                                            </option>
                                        ))}
                                    </select>
                                )}

                                {applyScope === "range" && (
                                    <>
                                        <span className="text-sm text-gray-500">〜</span>
                                        <select
                                            className={UI_SELECT_CTRL}
                                            value={applyToYm}
                                            onChange={(e) => setApplyToYm(e.target.value)}
                                        >
                                            {monthKeyList.map((ym) => (
                                                <option key={ym} value={ym}>
                                                    {dayjs(`${ym}-01`).format("YYYY/MM")}
                                                </option>
                                            ))}
                                        </select>
                                    </>
                                )}
                            </div>

                            <div className="border rounded bg-white p-2 flex flex-col gap-3">
                                {isRestrictedPlan ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                                        <div>
                                            <label className="text-sm text-gray-600">平均物流費（税込）</label>
                                            <input
                                                type="number"
                                                className="w-full border px-2 mt-1 bg-white text-sm h-7"
                                                value={tplLogisticsCost}
                                                onChange={(e) => setTplLogisticsCost(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                                        <div>
                                            <label className="text-sm text-gray-600">宅急便送料（税込）</label>
                                            <input
                                                type="number"
                                                className="w-full border px-2 mt-1 bg-white text-sm h-7"
                                                value={tplCourierShippingCost}
                                                onChange={(e) => setTplCourierShippingCost(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm text-gray-600">メール便送料（税込）</label>
                                            <input
                                                type="number"
                                                className="w-full border px-2 mt-1 bg-white text-sm h-7"
                                                value={tplMailShippingCost}
                                                onChange={(e) => setTplMailShippingCost(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}
                                {activeAdSlots > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                                        {Array.from({ length: activeAdSlots }, (_, idx) => (
                                            <div key={idx}>
                                                <label className="text-sm text-gray-600">
                                                    {adSlotNames[idx] || `楽天外広告${idx + 1}`}（税込）
                                                </label>
                                                <input
                                                    type="number"
                                                    className="w-full border px-2 mt-1 bg-white text-sm h-7"
                                                    value={tplAdCosts[idx]}
                                                    onChange={(e) => setTplAdCosts((prev) => {
                                                        const next: [string, string, string] = [...prev] as [string, string, string];
                                                        next[idx] = e.target.value;
                                                        return next;
                                                    })}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="mt-2 flex gap-2">
                                <button
                                    onClick={applyTemplateToRange}
                                    className={UI_BTN_PRIMARY}
                                    disabled={missingShopUrl}
                                >
                                    この複数月一括適用を範囲に適用
                                </button>
                            </div>

                            <div className="text-xs text-gray-500 mt-2">
                                ※月ごとに変更したい場合は、下の表で各月セルを直接変更してください。<br />
                                ※下部の表の中身が変動費計算に適用されます。
                            </div>
                        </>
                    )}
                </div>

                {/* ① 楽天外広告費テーブル */}
                <div className="mb-1 text-sm font-semibold text-gray-700">① 楽天外広告費（月額・税込）</div>
                <div className="mb-1 text-xs text-gray-500">
                    月額の下の「開始日〜終了日」は日次利益への按分期間です（例: 1〜15 → 1日〜15日の15日間に均等按分、16日以降は0）。
                    空欄の場合は月の全日に均等按分します。月次利益には期間に関係なく月額がそのまま計上されます。
                </div>
                <div className="overflow-x-auto border rounded relative mb-4">
                    <table className={TABLE_CLASS}>
                        <thead className="bg-gray-100">
                            <tr>
                                <th className={`text-left p-1 border ${STICKY_TH}`}>科目名（自由入力）</th>
                                {months.map((m) => (
                                    <th key={m.format("YYYY-MM")} className={`text-center ${TH_CELL}`}>
                                        {m.format("YYYY/MM")}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: activeAdSlots }, (_, idx) => {
                                const slotKey = `non_rakuten_ad_${idx + 1}_cost` as string;
                                return (
                                    <tr key={`ad_slot_${idx}`}>
                                        <LeftCell>
                                            <input
                                                type="text"
                                                className="w-full border px-2 py-0.5 text-xs h-7 rounded"
                                                placeholder={`例: メタ広告`}
                                                value={adSlotNames[idx]}
                                                onChange={(e) => {
                                                    setDirty(true);
                                                    setAdSlotNames((prev) => {
                                                        const next: [string, string, string] = [...prev] as [string, string, string];
                                                        next[idx] = e.target.value;
                                                        return next;
                                                    });
                                                }}
                                                disabled={missingShopUrl}
                                            />
                                        </LeftCell>
                                        {monthKeyList.map((ym) => {
                                            const dim = dayjs(`${ym}-01`).daysInMonth();
                                            const startKey = `non_rakuten_ad_${idx + 1}_start_day`;
                                            const endKey = `non_rakuten_ad_${idx + 1}_end_day`;
                                            return (
                                                <td key={ym} className={TD_CELL}>
                                                    <input
                                                        type="number"
                                                        className={UI_INPUT_NUM_CELL}
                                                        value={dataByMonth[ym]?.[slotKey] ?? ""}
                                                        onChange={(e) => handleIntChange(ym, slotKey, e.target.value)}
                                                        disabled={missingShopUrl}
                                                    />
                                                    <div
                                                        className="mt-0.5 flex items-center justify-end gap-0.5 text-[10px] text-gray-500 whitespace-nowrap"
                                                        title="日次利益への按分期間（空欄=月全体）"
                                                    >
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={dim}
                                                            placeholder="1"
                                                            className="w-9 border px-0.5 h-5 text-right text-[10px]"
                                                            value={dataByMonth[ym]?.[startKey] ?? ""}
                                                            onChange={(e) => handleDayChange(ym, startKey, e.target.value, dim)}
                                                            disabled={missingShopUrl}
                                                        />
                                                        <span>〜</span>
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={dim}
                                                            placeholder={String(dim)}
                                                            className="w-9 border px-0.5 h-5 text-right text-[10px]"
                                                            value={dataByMonth[ym]?.[endKey] ?? ""}
                                                            onChange={(e) => handleDayChange(ym, endKey, e.target.value, dim)}
                                                            disabled={missingShopUrl}
                                                        />
                                                        <span>日</span>
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                            {activeAdSlots < 3 && (
                                <tr>
                                    <td className="p-1 border bg-white" colSpan={months.length + 1}>
                                        <button
                                            type="button"
                                            className="text-xs text-blue-600 border border-dashed border-blue-300 rounded px-3 py-1 bg-blue-50 hover:bg-blue-100"
                                            onClick={() => {
                                                setDirty(true);
                                                setActiveAdSlots((v) => Math.min(v + 1, 3));
                                            }}
                                            disabled={missingShopUrl}
                                        >
                                            ＋ 科目を追加
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {activeAdSlots === 0 && (
                                <tr>
                                    <td className="p-2 border bg-white text-xs text-gray-400" colSpan={months.length + 1}>
                                        楽天外の広告費（Meta広告・Instagram広告など）を追加できます。最大3科目。
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ②③ 販促費・広告費の確定値テーブル */}
                {[
                    { no: "②", title: "販促費 確定値（請求額で上書き）", items: RAKUTEN_PROMO_ITEMS },
                    { no: "③", title: "広告費 確定値（請求額で上書き）", items: RAKUTEN_AD_ITEMS },
                ].map(renderConfirmedGroup)}

                {/* ④ 物流費テーブル */}
                <div className="mb-1 text-sm font-semibold text-gray-700">④ 物流費（1配送あたりの平均金額・税込）</div>
                <div className="overflow-x-auto border rounded relative">
                    <table className={TABLE_CLASS}>
                        <thead className="bg-gray-100">
                            <tr>
                                <th className={`text-left p-1 border ${STICKY_TH}`}>科目</th>
                                {months.map((m) => (
                                    <th key={m.format("YYYY-MM")} className={`text-center ${TH_CELL}`}>
                                        {m.format("YYYY/MM")}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {isRestrictedPlan ? (
                                <tr>
                                    <LeftCell>平均物流費（税込）</LeftCell>
                                    {monthKeyList.map((ym) => (
                                        <td key={ym} className={TD_CELL}>
                                            <input
                                                type="number"
                                                className={UI_INPUT_NUM_CELL}
                                                value={dataByMonth[ym]?.logistics_cost ?? ""}
                                                onChange={(e) => handleIntChange(ym, "logistics_cost", e.target.value)}
                                                disabled={missingShopUrl}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ) : (
                                <>
                                    <tr>
                                        <LeftCell>宅急便送料（税込）</LeftCell>
                                        {monthKeyList.map((ym) => (
                                            <td key={ym} className={TD_CELL}>
                                                <input
                                                    type="number"
                                                    className={UI_INPUT_NUM_CELL}
                                                    value={dataByMonth[ym]?.courier_shipping_cost ?? ""}
                                                    onChange={(e) => handleIntChange(ym, "courier_shipping_cost", e.target.value)}
                                                    disabled={missingShopUrl}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <LeftCell>メール便送料（税込）</LeftCell>
                                        {monthKeyList.map((ym) => (
                                            <td key={ym} className={TD_CELL}>
                                                <input
                                                    type="number"
                                                    className={UI_INPUT_NUM_CELL}
                                                    value={dataByMonth[ym]?.mail_shipping_cost ?? ""}
                                                    onChange={(e) => handleIntChange(ym, "mail_shipping_cost", e.target.value)}
                                                    disabled={missingShopUrl}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                </>
                            )}

                            {/* 物流費合計 */}
                            <tr className={TD_CELL_GRAY}>
                                <LeftCell gray className="font-bold">
                                    合計（物流費合計）
                                </LeftCell>
                                {monthKeyList.map((ym) => (
                                    <td key={ym} className={`${TD_CELL_GRAY} text-right font-bold`}>
                                        {totalsByMonth[ym]?.toLocaleString()} 円
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="mt-4" />

                {/* ⑤ 物流費 確定値テーブル */}
                {renderConfirmedGroup({
                    no: "⑤",
                    title: "物流費 確定値（請求額で上書き）",
                    items: RAKUTEN_SHIPPING_ITEMS,
                    help: (
                        <div className="text-xs leading-relaxed">
                            月次の物流費を請求額ベースで確定できます。<br />
                            「配送料（月合計）」を入力した月は、④の単価から推計した配送料に代えて入力値で月次利益を計算します。<br />
                            「その他物流費」（出荷作業料・資材費・保管料など）は入力した月の物流費にそのまま加算されます。<br />
                            ※確定値は月次利益にのみ反映されます（日次利益は速報値のままです）。<br />
                            ※金額は店舗の税表示設定（税込/税抜）に合わせて入力してください。
                        </div>
                    ),
                })}

                <div className="mt-4" />

                {/* ⑥ 過去ポイント登録（日別） */}
                <div className="mb-1 text-sm font-semibold text-gray-700 flex items-center gap-1">
                    ⑥ 過去ポイント登録（日別）
                    <HelpTip
                        text={
                            <div className="text-xs leading-relaxed">
                                RMSのポイント付与明細は過去6ヶ月分しか取得できないため、それより前の期間はここから日別で登録できます。<br />
                                登録したポイントは自動取得分と同じ扱いで保存され、月次利益・日次利益・商品別利益（売上按分）に反映されます。<br />
                                直近6ヶ月（{rpBoundary.format("YYYY/MM")} 以降）はRMSから自動取得されるため、ここでは登録できません。<br />
                                件数・金額とも空欄で保存すると、その日の登録を削除します。
                            </div>
                        }
                    />
                    <button
                        type="button"
                        className="ml-2 text-xs px-2 py-0.5 border rounded bg-white hover:bg-gray-50"
                        onClick={() => setRpOpen((v) => !v)}
                    >
                        {rpOpen ? "閉じる" : "開く"}
                    </button>
                </div>

                {rpOpen && (
                    <div className="border rounded p-3 mb-2 max-w-[640px] relative">
                        {rpLoading && <LoadingInline />}

                        <div className="flex flex-wrap items-center gap-2 mb-2">
                            <label className="text-sm text-gray-600">対象月</label>
                            <select
                                className={UI_SELECT_CTRL}
                                value={rpMonth}
                                onChange={(e) => setRpMonth(e.target.value)}
                                disabled={rpLoading || rpSaving}
                            >
                                {rpMonthOptions.map((m) => (
                                    <option key={m} value={m}>
                                        {m.replace("-", "/")}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className={UI_BTN_PRIMARY}
                                onClick={handleRpSave}
                                disabled={missingShopUrl || rpLoading || rpSaving || !rpDirty}
                            >
                                {rpSaving ? "保存中..." : "この月を保存"}
                            </button>
                            {rpDirty && !rpSaving && (
                                <span className="text-xs text-amber-700">未保存の変更があります</span>
                            )}
                        </div>

                        {rpKarteMinDate && `${rpMonth}-01` < rpKarteMinDate && (
                            <div className="mb-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                                この店舗の売上データは {rpKarteMinDate.replace(/-/g, "/")} 以降のみ取得されています。
                                それより前の日付にポイントを登録しても、売上データがないため利益画面には反映されません。
                            </div>
                        )}

                        <div className="mb-2">
                            <div className="text-xs text-gray-600 mb-1">
                                RMSのポイント付与明細（日付・付与件数・付与ポイント）をコピーして貼り付けると一括入力できます。
                            </div>
                            <div className="flex items-start gap-2">
                                <textarea
                                    className="border rounded px-2 py-1 text-xs w-full h-16 font-mono"
                                    placeholder={"例)\n2025/01/15(水)\t123\t4,567\n2025/01/16(木)\t98\t3,210"}
                                    value={rpPaste}
                                    onChange={(e) => setRpPaste(e.target.value)}
                                    disabled={missingShopUrl || rpSaving}
                                />
                                <button
                                    type="button"
                                    className={`${UI_BTN_SM} whitespace-nowrap text-xs`}
                                    onClick={handleRpApplyPaste}
                                    disabled={missingShopUrl || rpSaving || rpPaste.trim() === ""}
                                >
                                    貼り付けを反映
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto border rounded">
                            <table className="w-full text-sm border-separate border-spacing-0">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className={`text-left ${TH_CELL}`}>日付</th>
                                        <th className={`text-right ${TH_CELL}`}>付与件数</th>
                                        <th className={`text-right ${TH_CELL}`}>付与ポイント（円）</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rpDateList.map((date) => {
                                        const d = dayjs(date);
                                        const wd = RP_WEEKDAYS[d.day()];
                                        return (
                                            <tr key={date}>
                                                <td className={`p-1 border whitespace-nowrap ${d.day() === 0 ? "text-red-600" : d.day() === 6 ? "text-blue-600" : ""}`}>
                                                    {d.format("MM/DD")}（{wd}）
                                                </td>
                                                <td className={TD_CELL}>
                                                    <input
                                                        type="number"
                                                        className={UI_INPUT_NUM_CELL}
                                                        value={rpDays[date]?.point_count ?? ""}
                                                        onChange={(e) => handleRpChange(date, "point_count", e.target.value)}
                                                        disabled={missingShopUrl || rpSaving}
                                                    />
                                                </td>
                                                <td className={TD_CELL}>
                                                    <input
                                                        type="number"
                                                        className={UI_INPUT_NUM_CELL}
                                                        value={rpDays[date]?.point_amount ?? ""}
                                                        onChange={(e) => handleRpChange(date, "point_amount", e.target.value)}
                                                        disabled={missingShopUrl || rpSaving}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    <tr>
                                        <td className="p-1 border bg-gray-50 font-bold">合計</td>
                                        <td className="p-1 border bg-gray-50" />
                                        <td className="p-1 border bg-gray-50 text-right font-bold pr-1">
                                            {rpMonthTotal.toLocaleString()} 円
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div className="text-xs text-gray-500 mt-2">
                    ※「年度」は表示期間の開始年の意味。期首月が3月なら、2024年度＝2024/03〜2025/02 になります。
                </div>

            </div>
        </div>
    );
}
